import type { SearchSchemaInput } from '@tanstack/react-router';
import { parseEnum, validateListSearch, type ListSearch } from '@/shared/lib/listSearch';
import {
  PlayersControllerListPlayersV1ContractStatus,
  PlayersControllerListPlayersV1Position,
  PlayersControllerListPlayersV1Representation,
  PlayersControllerListPlayersV1SortBy,
  type PlayersControllerListPlayersV1Params,
} from '@/api/generated/model';

/**
 * URL state for the players table.
 *
 * Every filter lives in the URL rather than in `useState`, which is what makes
 * a filtered view shareable, refresh-proof and back-button-correct — and it is
 * also what makes the "Share view" button a clipboard copy rather than a
 * feature with its own storage.
 *
 * The enums come from the generated model rather than being re-typed, so a
 * position or sort field added on the backend surfaces as a compile error
 * wherever it is switched on, not as a silently unhandled value.
 */
export type PlayerPosition = PlayersControllerListPlayersV1Position;
export type PlayerSortField = PlayersControllerListPlayersV1SortBy;
export type PlayerRepresentation = PlayersControllerListPlayersV1Representation;
export type PlayerContractStatus = PlayersControllerListPlayersV1ContractStatus;

export interface PlayersSearch extends Omit<ListSearch, 'sortBy'> {
  sortBy?: PlayerSortField | undefined;
  position?: PlayerPosition | undefined;
  /**
   * Whether `position` also matches players who merely cover it.
   *
   * Defaults to **true**, matching the backend. Kept out of the URL when true
   * so the common case produces a clean link — only the deliberate narrowing
   * is worth carrying around.
   */
  includeSecondaryPositions: boolean;
  clubId?: string | undefined;
  leagueId?: string | undefined;
  nationality?: string | undefined;
  representation?: PlayerRepresentation | undefined;
  contractStatus?: PlayerContractStatus | undefined;
  ageMin?: number | undefined;
  ageMax?: number | undefined;
  contractExpiryFrom?: string | undefined;
  contractExpiryTo?: string | undefined;
  shortlistId?: string | undefined;
}

/**
 * Pitch positions, ordered back to front rather than alphabetically.
 *
 * A goalkeeper-to-striker ordering is how anyone in football reads a position
 * list; alphabetical would open with "AM, CB, CM, DM" and be slower to scan for
 * every single user.
 */
export const PLAYER_POSITIONS = [
  PlayersControllerListPlayersV1Position.GK,
  PlayersControllerListPlayersV1Position.CB,
  PlayersControllerListPlayersV1Position.LB,
  PlayersControllerListPlayersV1Position.RB,
  PlayersControllerListPlayersV1Position.DM,
  PlayersControllerListPlayersV1Position.CM,
  PlayersControllerListPlayersV1Position.AM,
  PlayersControllerListPlayersV1Position.LW,
  PlayersControllerListPlayersV1Position.RW,
  PlayersControllerListPlayersV1Position.ST,
] as const;

export const PLAYER_REPRESENTATIONS = [
  PlayersControllerListPlayersV1Representation.signed,
  PlayersControllerListPlayersV1Representation.prospect,
  PlayersControllerListPlayersV1Representation.other_agency,
  PlayersControllerListPlayersV1Representation.unrepresented,
] as const;

export const PLAYER_CONTRACT_STATUSES = [
  PlayersControllerListPlayersV1ContractStatus.active,
  PlayersControllerListPlayersV1ContractStatus.expiring,
  PlayersControllerListPlayersV1ContractStatus.expired,
  PlayersControllerListPlayersV1ContractStatus.none,
] as const;

const SORT_FIELDS = [
  PlayersControllerListPlayersV1SortBy.recent,
  PlayersControllerListPlayersV1SortBy.name,
  PlayersControllerListPlayersV1SortBy.age,
  PlayersControllerListPlayersV1SortBy.marketValue,
  PlayersControllerListPlayersV1SortBy.weeklyWage,
  PlayersControllerListPlayersV1SortBy.contractUntil,
] as const;

/**
 * The sort control offers presets rather than a field plus a direction.
 *
 * Nobody wants the cheapest player first, so pairing every field with both
 * directions would mostly offer combinations that are never picked. The URL
 * still carries the backend's own `sortBy`/`sortOrder`; the preset is an
 * affordance over them, not a third vocabulary.
 */
export const PLAYER_SORT_PRESETS = [
  { key: 'marketValue', sortBy: SORT_FIELDS[3], sortOrder: 'desc' },
  { key: 'recent', sortBy: SORT_FIELDS[0], sortOrder: 'desc' },
  { key: 'name', sortBy: SORT_FIELDS[1], sortOrder: 'asc' },
  { key: 'youngest', sortBy: SORT_FIELDS[2], sortOrder: 'asc' },
  { key: 'weeklyWage', sortBy: SORT_FIELDS[4], sortOrder: 'desc' },
  { key: 'contractUntil', sortBy: SORT_FIELDS[5], sortOrder: 'asc' },
] as const satisfies readonly {
  key: string;
  sortBy: PlayerSortField;
  sortOrder: 'asc' | 'desc';
}[];

export type PlayerSortPresetKey = (typeof PLAYER_SORT_PRESETS)[number]['key'];

/** The preset the current URL corresponds to; the default when it matches none. */
export function playerSortPresetKey(search: PlayersSearch): PlayerSortPresetKey {
  const match = PLAYER_SORT_PRESETS.find(
    (preset) => preset.sortBy === search.sortBy && preset.sortOrder === search.sortOrder,
  );
  return match?.key ?? 'marketValue';
}

export function playerSortPreset(
  key: PlayerSortPresetKey,
): (typeof PLAYER_SORT_PRESETS)[number] {
  return PLAYER_SORT_PRESETS.find((preset) => preset.key === key) ?? PLAYER_SORT_PRESETS[0];
}

/**
 * The value a filter control carries when it is not filtering.
 *
 * antd's `Select` cannot use `undefined` as an option value — an option with no
 * value is unselectable — so "show everything" needs a sentinel. It never
 * reaches the URL or the API: `clearAny` in PlayerFilters maps it back to
 * `undefined` before anything else sees it.
 *
 * Deliberately not a string a real value could collide with: positions are two
 * letters, country codes three, and ids are uuids.
 */
export const ANY = '__any__';

/** The bounds the age filter offers, matching the backend's validation. */
export const AGE_BOUNDS = { min: 14, max: 60 } as const;

/**
 * Coerces raw URL params into `PlayersSearch`.
 *
 * Defensive throughout, because search params are user input and the backend
 * validates with `forbidNonWhitelisted` — a hand-edited `?position=SW` would
 * come back as a 400 the user can do nothing about, rather than being ignored.
 * Anything unrecognised is dropped here instead of reaching the request.
 */
export function validatePlayersSearch(
  search: Partial<Record<keyof PlayersSearch, unknown>> & SearchSchemaInput,
): PlayersSearch {
  const base = validateListSearch(search);
  const sortBy = parseEnum(base.sortBy, SORT_FIELDS);

  // Swapped rather than rejected: someone typing 30 then 20 into the two age
  // boxes means the same thing as 20 then 30, and an empty table would just
  // look broken.
  const [ageMin, ageMax] = orderedAges(
    toAge(search.ageMin),
    toAge(search.ageMax),
  );

  return {
    ...base,
    sortBy,
    sortOrder: sortBy ? base.sortOrder : undefined,
    position: parseEnum(search.position, PLAYER_POSITIONS),
    // Only an explicit `false` narrows it; anything else means the default.
    includeSecondaryPositions: search.includeSecondaryPositions !== 'false',
    clubId: toId(search.clubId),
    leagueId: toId(search.leagueId),
    nationality: toCountryCode(search.nationality),
    representation: parseEnum(search.representation, PLAYER_REPRESENTATIONS),
    contractStatus: parseEnum(search.contractStatus, PLAYER_CONTRACT_STATUSES),
    ageMin,
    ageMax,
    contractExpiryFrom: toDate(search.contractExpiryFrom),
    contractExpiryTo: toDate(search.contractExpiryTo),
    shortlistId: toId(search.shortlistId),
  };
}

/**
 * Maps URL state onto the generated query params.
 *
 * The names already match the backend's, so this only drops empty keys and the
 * default. Sending `search: ''` would filter for the empty string rather than
 * for the absence of a filter.
 */
export function toPlayersParams(
  search: PlayersSearch,
): PlayersControllerListPlayersV1Params {
  return {
    page: search.page,
    limit: search.limit,
    ...(search.sortBy && search.sortOrder
      ? { sortBy: search.sortBy, sortOrder: search.sortOrder }
      : {}),
    ...(search.search ? { search: search.search } : {}),
    ...(search.position ? { position: search.position } : {}),
    // Only sent when it differs from the backend's default, keeping the common
    // request — and the shareable URL — free of noise.
    ...(search.includeSecondaryPositions ? {} : { includeSecondaryPositions: false }),
    ...(search.clubId ? { clubId: search.clubId } : {}),
    ...(search.leagueId ? { leagueId: search.leagueId } : {}),
    ...(search.nationality ? { nationality: search.nationality } : {}),
    ...(search.representation ? { representation: search.representation } : {}),
    ...(search.contractStatus ? { contractStatus: search.contractStatus } : {}),
    ...(search.ageMin !== undefined ? { ageMin: search.ageMin } : {}),
    ...(search.ageMax !== undefined ? { ageMax: search.ageMax } : {}),
    ...(search.contractExpiryFrom ? { contractExpiryFrom: search.contractExpiryFrom } : {}),
    ...(search.contractExpiryTo ? { contractExpiryTo: search.contractExpiryTo } : {}),
    ...(search.shortlistId ? { shortlistId: search.shortlistId } : {}),
  };
}

/** True when anything beyond paging and sorting is narrowing the list. */
export function hasActiveFilters(search: PlayersSearch): boolean {
  return Boolean(
    search.search ||
      search.position ||
      search.clubId ||
      search.leagueId ||
      search.nationality ||
      search.representation ||
      search.contractStatus ||
      search.ageMin !== undefined ||
      search.ageMax !== undefined ||
      search.contractExpiryFrom ||
      search.contractExpiryTo ||
      search.shortlistId,
  );
}

/**
 * Clears every filter while leaving sorting and the shortlist alone.
 *
 * Returns the keys explicitly set to `undefined` rather than an empty object,
 * because the caller spreads this over the existing search: omitting a key
 * would keep its old value, which is the opposite of clearing it.
 */
export function clearedFilters(): Partial<PlayersSearch> {
  return {
    page: 1,
    search: undefined,
    position: undefined,
    clubId: undefined,
    leagueId: undefined,
    nationality: undefined,
    representation: undefined,
    contractStatus: undefined,
    ageMin: undefined,
    ageMax: undefined,
    contractExpiryFrom: undefined,
    contractExpiryTo: undefined,
  };
}

function orderedAges(
  min: number | undefined,
  max: number | undefined,
): [number | undefined, number | undefined] {
  if (min !== undefined && max !== undefined && min > max) return [max, min];
  return [min, max];
}

function toAge(value: unknown): number | undefined {
  const parsed = typeof value === 'number' ? value : Number(value);
  if (!Number.isInteger(parsed)) return undefined;
  return parsed >= AGE_BOUNDS.min && parsed <= AGE_BOUNDS.max ? parsed : undefined;
}

function toId(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;
}

/** Three letters, upper-cased. The backend matches the code exactly. */
function toCountryCode(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim().toUpperCase();
  return /^[A-Z]{3}$/.test(trimmed) ? trimmed : undefined;
}

/** `YYYY-MM-DD`, which is what the backend's `@IsDateString` accepts. */
function toDate(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(trimmed) ? trimmed : undefined;
}
